import config from "@/config";
import { useFrontChatBoot } from "front-chat-sdk/hooks";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@babackapp/auth-client/hooks";
import { useFrontChatHash } from "@babackapp/private-api-client/client/use-frontchat-hash";

// Ce champ doit exister dans Front (Settings > Contacts > Custom fields),
// sinon il est ignoré silencieusement.
const CURRENT_URL_FIELD = "Current URL";

export function FrontChat() {
  const { frontChat, initialize, isInitialized } = useFrontChatBoot(
    document.body,
  );
  const { user } = useAuth();
  const frontChatHashQuery = useFrontChatHash();
  const location = useLocation();

  const email = user?.emails[0]?.address;
  const userHash = frontChatHashQuery.data?.frontHash;

  // `useFrontChatBoot` recrée `frontChat` à chaque render (pas de useCallback
  // dans le SDK). Le garder hors des dépendances évite de relancer l'effet
  // d'URL à chaque render du layout.
  const frontChatRef = useRef(frontChat);
  useEffect(() => {
    frontChatRef.current = frontChat;
  });

  // Dernière URL réellement transmise à Front, pour ne pas répéter l'appel.
  const lastSentUrlRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (
      !config.isProd ||
      !initialize ||
      isInitialized ||
      !user ||
      frontChatHashQuery.isLoading
    ) {
      return;
    }

    lastSentUrlRef.current = window.location.href;

    initialize({
      chatId: config.frontchatId,
      userHash,
      useDefaultLauncher: true,
      email,
      name: `${user.profile?.firstName || ""} ${user.profile?.lastName || ""}`.trim(),
      customFields: {
        [CURRENT_URL_FIELD]: lastSentUrlRef.current,
      },
    });
  }, [
    isInitialized,
    initialize,
    frontChatHashQuery.isLoading,
    config.frontchatId,
  ]);

  // Garde l'URL à jour à chaque navigation. `identity` remplace l'identité du
  // contact côté Front : sans email + userHash, Front ne sait pas à quel
  // contact rattacher les customFields et les ignore silencieusement. On
  // repasse donc les mêmes identifiants qu'à l'initialisation.
  useEffect(() => {
    if (!isInitialized || !email || !userHash) {
      return;
    }

    const currentUrl = window.location.href;
    if (lastSentUrlRef.current === currentUrl) {
      return;
    }
    lastSentUrlRef.current = currentUrl;

    frontChatRef.current?.("identity", {
      email,
      userHash,
      customFields: {
        [CURRENT_URL_FIELD]: currentUrl,
      },
    });
  }, [
    isInitialized,
    email,
    userHash,
    location.pathname,
    location.search,
    location.hash,
  ]);

  return <></>;
}
